const LS_KEY = 'cotizacion.importacionPresupuestos.prefs';
const IDB_NAME = 'eone-importacion-presupuestos';
const IDB_STORE = 'handles';
const IDB_HANDLE_KEY = 'carpeta';

export type ImportacionPresupuestosPrefs = {
  nroSuc: number;
  nroUbicacion: number;
  ubicacionLabel: string;
  carpetaNombre: string;
};

type DirectoryHandleLike = FileSystemDirectoryHandle;

function emptyPrefs(): ImportacionPresupuestosPrefs {
  return { nroSuc: 0, nroUbicacion: 0, ubicacionLabel: '', carpetaNombre: '' };
}

export function loadImportacionPresupuestosPrefs(): ImportacionPresupuestosPrefs {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (!raw) return emptyPrefs();
    const parsed = JSON.parse(raw) as Partial<ImportacionPresupuestosPrefs>;
    const nroSuc = Number(parsed.nroSuc);
    const nroUbicacion = Number(parsed.nroUbicacion);
    return {
      nroSuc: Number.isFinite(nroSuc) && nroSuc > 0 ? nroSuc : 0,
      nroUbicacion: Number.isFinite(nroUbicacion) && nroUbicacion > 0 ? nroUbicacion : 0,
      ubicacionLabel: typeof parsed.ubicacionLabel === 'string' ? parsed.ubicacionLabel : '',
      carpetaNombre: typeof parsed.carpetaNombre === 'string' ? parsed.carpetaNombre : '',
    };
  } catch {
    return emptyPrefs();
  }
}

export function saveImportacionPresupuestosPrefs(
  prefs: Partial<ImportacionPresupuestosPrefs>,
): ImportacionPresupuestosPrefs {
  const current = loadImportacionPresupuestosPrefs();
  const next: ImportacionPresupuestosPrefs = {
    nroSuc: prefs.nroSuc != null && prefs.nroSuc > 0 ? prefs.nroSuc : current.nroSuc,
    nroUbicacion:
      prefs.nroUbicacion != null && prefs.nroUbicacion > 0
        ? prefs.nroUbicacion
        : current.nroUbicacion,
    ubicacionLabel:
      prefs.ubicacionLabel != null ? String(prefs.ubicacionLabel) : current.ubicacionLabel,
    carpetaNombre:
      prefs.carpetaNombre != null ? String(prefs.carpetaNombre) : current.carpetaNombre,
  };
  try {
    localStorage.setItem(LS_KEY, JSON.stringify(next));
  } catch {
    // ignore quota / private mode
  }
  return next;
}

function openHandlesDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(IDB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('No se pudo abrir IndexedDB'));
  });
}

export async function saveImportacionCarpetaHandle(
  handle: DirectoryHandleLike,
): Promise<void> {
  const db = await openHandlesDb();
  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, 'readwrite');
      tx.objectStore(IDB_STORE).put(handle, IDB_HANDLE_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('No se pudo guardar la carpeta'));
    });
  } finally {
    db.close();
  }
}

export async function loadImportacionCarpetaHandle(): Promise<DirectoryHandleLike | null> {
  try {
    const db = await openHandlesDb();
    try {
      return await new Promise<DirectoryHandleLike | null>((resolve, reject) => {
        const tx = db.transaction(IDB_STORE, 'readonly');
        const req = tx.objectStore(IDB_STORE).get(IDB_HANDLE_KEY);
        req.onsuccess = () => {
          const value = req.result;
          resolve(value && typeof value === 'object' ? (value as DirectoryHandleLike) : null);
        };
        req.onerror = () => reject(req.error ?? new Error('No se pudo leer la carpeta'));
      });
    } finally {
      db.close();
    }
  } catch {
    return null;
  }
}

export async function ensureDirectoryReadPermission(
  handle: DirectoryHandleLike,
): Promise<boolean> {
  const opts = { mode: 'read' as const };
  const anyHandle = handle as DirectoryHandleLike & {
    queryPermission?: (o: { mode: 'read' }) => Promise<PermissionState>;
    requestPermission?: (o: { mode: 'read' }) => Promise<PermissionState>;
  };
  try {
    if (typeof anyHandle.queryPermission === 'function') {
      const q = await anyHandle.queryPermission(opts);
      if (q === 'granted') return true;
    }
    if (typeof anyHandle.requestPermission === 'function') {
      const r = await anyHandle.requestPermission(opts);
      return r === 'granted';
    }
    return true;
  } catch {
    return false;
  }
}

export async function listarPdfsDesdeHandle(handle: DirectoryHandleLike): Promise<File[]> {
  const files: File[] = [];
  const anyHandle = handle as DirectoryHandleLike & {
    values?: () => AsyncIterableIterator<{ kind: string; name: string; getFile?: () => Promise<File> }>;
  };
  if (typeof anyHandle.values !== 'function') return files;

  for await (const entry of anyHandle.values()) {
    if (entry.kind !== 'file') continue;
    const name = String(entry.name || '');
    if (!name.toLowerCase().endsWith('.pdf')) continue;
    if (typeof entry.getFile !== 'function') continue;
    files.push(await entry.getFile());
  }
  return files.sort((a, b) => a.name.localeCompare(b.name));
}
